import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, ArrowRight, AlertCircle, Download, Send } from 'lucide-react';

const PermitApplications = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    permitType: '',
    facilityType: '',
    projectLocation: '',
    operationType: '',
    emissionSources: '',
    potentialEmissions: '',
    controlTechnology: '',
    timeline: '',
    existingPermits: '',
    previousViolations: '',
    specificRequirements: '',
    additionalInfo: ''
  });

  const permitTypes = [
    { value: '', label: 'Select permit type' },
    { value: 'title-v', label: 'Title V Operating Permit' },
    { value: 'minor-source', label: 'Minor Source Permit' },
    { value: 'psd', label: 'Prevention of Significant Deterioration (PSD)' },
    { value: 'synthetic-minor', label: 'Synthetic Minor Permit' },
    { value: 'general-permit', label: 'General Permit' },
    { value: 'nsr', label: 'New Source Review (NSR)' },
    { value: 'other', label: 'Other' }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-generation', label: 'Power Generation Facility' },
    { value: 'oil-gas', label: 'Oil & Gas Operation' },
    { value: 'military', label: 'Military Installation' },
    { value: 'seafood', label: 'Seafood Processing Plant' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'construction', label: 'Construction/Asphalt Plant' },
    { value: 'remediation', label: 'Soil Remediation Facility' },
    { value: 'geothermal', label: 'Geothermal Operation' },
    { value: 'other', label: 'Other Industrial Facility' }
  ];

  const operationTypes = [
    { value: '', label: 'Select operation type' },
    { value: 'new-construction', label: 'New Construction' },
    { value: 'modification', label: 'Facility Modification' },
    { value: 'expansion', label: 'Facility Expansion' },
    { value: 'renewal', label: 'Permit Renewal' },
    { value: 'revision', label: 'Permit Revision' }
  ];

  const timelines = [
    { value: '', label: 'Select timeline' },
    { value: 'immediate', label: 'Immediate (Within 1 month)' },
    { value: '3-months', label: '1-3 Months' },
    { value: '6-months', label: '3-6 Months' },
    { value: '12-months', label: '6-12 Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/permit-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your permit application inquiry has been submitted successfully. We will contact you shortly.');
        // Reset form
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          permitType: '', facilityType: '', projectLocation: '', operationType: '',
          emissionSources: '', potentialEmissions: '', controlTechnology: '',
          existingPermits: '', previousViolations: '', timeline: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your inquiry. Please try again or contact us directly.');
    }
  };

  const requiredDocuments = [
    'Project Description and Technical Specifications',
    'Facility Plot Plan and Site Layout',
    'Process Flow Diagrams',
    'Emission Calculations and Estimates',
    'Air Quality Modeling (if required)',
    'Best Available Control Technology (BACT) Analysis',
    'Compliance Demonstration Documentation',
    'Monitoring and Recordkeeping Plans',
    'Emergency Response Plans',
    'Previous Permits and Compliance History'
  ];

  const applicationSteps = [
    {
      step: 'Initial Consultation',
      description: 'We assess your project needs, determine applicable regulations, and identify required permits.',
      duration: '1-2 weeks'
    },
    {
      step: 'Information Gathering',
      description: 'Collection of all technical data, facility specifications, and emission information.',
      duration: '2-4 weeks'
    },
    {
      step: 'Application Preparation',
      description: 'Comprehensive preparation of permit application package with all required documentation.',
      duration: '4-8 weeks'
    },
    {
      step: 'Technical Review',
      description: 'Internal review of application for completeness and accuracy before submission.',
      duration: '1-2 weeks'
    },
    {
      step: 'Agency Submission',
      description: 'Formal submission to Alaska DEC or EPA with all supporting documents.',
      duration: '1 week'
    },
    {
      step: 'Agency Review & Response',
      description: 'Responding to agency comments, providing additional information as requested.',
      duration: 'Varies (2-6 months)'
    },
    {
      step: 'Permit Issuance',
      description: 'Final permit approval and issuance with conditions and requirements.',
      duration: '1-2 weeks'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="permit-applications-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-emerald-900 to-emerald-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1640125346217-e4bb9313d6bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjBmb3Jlc3R8ZW58MHx8fHwxNzYxMTAxMTkxfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-6">
            <FileText className="w-16 h-16 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Environmental Permit Applications
              </h1>
              <p className="text-xl md:text-2xl max-w-3xl">
                Expert preparation and submission of environmental permit applications for Alaska operations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Alaska Air Permit Specialists</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Navigating Alaska's specific air quality regulations requires local expertise. We specialize in Title V, Minor Source, and General Permits, ensuring your facility is permitted correctly without over-regulation.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-emerald-50 p-6 rounded-lg border-l-4 border-emerald-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Title V & Major Sources</h3>
                <p className="text-gray-700">Expert preparation of Title V (Part 71/70) operating permits and PSD avoidance strategies.</p>
              </div>
              
              <div className="bg-emerald-50 p-6 rounded-lg border-l-4 border-emerald-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Minor Source Permitting</h3>
                <p className="text-gray-700">Streamlined applications for Article 5 Minor Source permits.</p>
              </div>
              
              <div className="bg-emerald-50 p-6 rounded-lg border-l-4 border-emerald-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Synthetic Minor Limits</h3>
                <p className="text-gray-700">Strategic development of Owner Requested Limits (ORLs) to avoid Title V classification.</p>
              </div>
              
              <div className="bg-emerald-50 p-6 rounded-lg border-l-4 border-emerald-700">
                <h3 className="text-xl font-bold text-gray-900 mb-3">General Permits (MG)</h3>
                <p className="text-gray-700">Fast-track permitting for MG1/MG2 (Oil & Gas) and MG3/MG9 (Rock Crushers & Asphalt).</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process Steps */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">The Permit Application Process</h2>
          <div className="max-w-5xl mx-auto space-y-6">
            {applicationSteps.map((step, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-l-4 border-emerald-700">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-emerald-700 text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.step}</h3>
                    <p className="text-gray-600 mb-2">{step.description}</p>
                    <p className="text-sm text-emerald-700 font-semibold">Typical Duration: {step.duration}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Required Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Required Documentation</h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-8">
              <div className="flex items-start">
                <AlertCircle className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0 mt-1" />
                <p className="text-gray-700">
                  <strong>Important:</strong> The specific documentation requirements vary based on your facility type, 
                  permit type, and project scope. We will work with you to identify all applicable requirements.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {requiredDocuments.map((doc, index) => (
                <div key={index} className="flex items-start bg-gray-50 p-4 rounded-lg">
                  <Download className="w-5 h-5 text-emerald-700 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{doc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Permit Types We Handle */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Types of Permits We Prepare</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Title V Operating Permits</h3>
              <p className="text-gray-600">
                Major source operating permits for facilities with potential emissions above federal thresholds. 
                Comprehensive permits covering all emission sources and regulatory requirements.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Minor Source Permits</h3>
              <p className="text-gray-600">
                State air quality permits for facilities below major source thresholds. Streamlined process with 
                Alaska-specific requirements and compliance obligations.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">PSD Permits</h3>
              <p className="text-gray-600">
                Prevention of Significant Deterioration permits for new major sources or major modifications. 
                Includes BACT analysis and air quality impact modeling.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">General Permits</h3>
              <p className="text-gray-600">
                Pre-approved permits for common source categories. Simplified application process for 
                qualifying facilities with standard operating parameters.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Synthetic Minor Permits</h3>
              <p className="text-gray-600">
                Permits with enforceable limits to remain below major source thresholds. Cost-effective 
                alternative avoiding Title V requirements.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Permit Modifications</h3>
              <p className="text-gray-600">
                Administrative, minor, and significant modifications to existing permits. Expert navigation 
                of modification procedures and timelines.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Inquiry Form */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-emerald-700 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Start Your Permit Application</h2>
                  <p className="text-emerald-100 mt-2">Provide details about your project for a customized consultation</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl">
              {/* Contact Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Project Information */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Permit Type Needed
                    </label>
                    <select
                      name="permitType"
                      value={formData.permitType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {permitTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Facility Type
                    </label>
                    <select
                      name="facilityType"
                      value={formData.facilityType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {facilityTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Project Location
                    </label>
                    <input
                      type="text"
                      name="projectLocation"
                      value={formData.projectLocation}
                      onChange={handleChange}
                      placeholder="City/Region in Alaska"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Operation Type
                    </label>
                    <select
                      name="operationType"
                      value={formData.operationType}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      {operationTypes.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Technical Details</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Emission Sources
                  </label>
                  <textarea
                    name="emissionSources"
                    value={formData.emissionSources}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe emission sources (e.g., engines, boilers, process equipment)"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Potential Emissions
                  </label>
                  <input
                    type="text"
                    name="potentialEmissions"
                    value={formData.potentialEmissions}
                    onChange={handleChange}
                    placeholder="e.g., NOx: 10 tons/year, PM: 5 tons/year"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Planned Control Technology
                  </label>
                  <textarea
                    name="controlTechnology"
                    value={formData.controlTechnology}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe emission control equipment or measures"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Compliance History */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Compliance History</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Existing Permits
                    </label>
                    <select
                      name="existingPermits"
                      value={formData.existingPermits}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes - Have existing permits</option>
                      <option value="no">No - New facility</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Previous Violations
                    </label>
                    <select
                      name="previousViolations"
                      value={formData.previousViolations}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Select option</option>
                      <option value="no">No violations</option>
                      <option value="yes">Yes - violations on record</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Timeline and Additional Info */}
              <div className="mb-8">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Timeline
                  </label>
                  <select
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    {timelines.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Requirements or Concerns <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="specificRequirements"
                    value={formData.specificRequirements}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="e.g., BACT requirements, modeling needs, timeline constraints, compliance concerns, etc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any additional details about your project, special requirements, or questions"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  ></textarea>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button
                  type="submit"
                  className="inline-flex items-center px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-colors shadow-lg"
                >
                  Submit Application Inquiry
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Need Help Understanding Your Permit Requirements?
          </h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Contact us for a free initial consultation to discuss your specific permit needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-emerald-700 text-white rounded-lg font-semibold hover:bg-emerald-800 transition-colors shadow-lg"
          >
            Contact Us
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PermitApplications;
